<template>
    <div class="push-guide push-guide--android position-relative overflow-hidden">
        <img class="push-guide__corner position-absolute top-0 end-0" src="/img/svg/nav-corner-1.svg" alt="">

        <section class="push-guide__hero container position-relative text-center">
            <p class="push-guide__eyebrow mb-3">{{$t('marketing.push_notifications_android.eyebrow')}}</p>
            <h1 id="android-push-guide-title" class="display-5 fw-bolder text-gray-900 mx-auto mb-3">
                {{$t('marketing.push_notifications_android.title')}}
            </h1>
            <p class="push-guide__subtitle fs-5 text-gray-600 mx-auto mb-3">
                {{$t('marketing.push_notifications_android.subtitle')}}
            </p>
            <router-link
            :to="{ name: 'push_notifications' }"
            class="push-guide__platform-link d-inline-flex align-items-center gap-2 mb-3"
            >
                {{$t('marketing.push_notifications_android.other_guide')}}
                <BootstrapIcon name="arrow-right" :size="15" aria-hidden="true" />
            </router-link>
            <p class="push-guide__screenshot-note small text-gray-500 mx-auto mb-0">
                {{$t('marketing.push_notifications_android.screenshot_note')}}
            </p>

            <aside class="push-guide__notice d-flex align-items-start gap-3 mx-auto text-start">
                <span class="push-guide__notice-icon flex-shrink-0" aria-hidden="true">
                    <BootstrapIcon name="info-circle" :size="20" />
                </span>
                <div>
                    <h2 class="h6 fw-bolder text-gray-900 mb-1">{{$t('marketing.push_notifications_android.before_title')}}</h2>
                    <p class="text-gray-600 mb-0">
                        {{$t('marketing.push_notifications_android.before_description_prefix')}}<a
                        class="fw-bolder text-teal-700"
                        :href="'https://store.' + TLD"
                        target="_blank"
                        rel="noopener"
                        >Tapgiant Delivery</a>{{$t('marketing.push_notifications_android.before_description_suffix')}}
                    </p>
                </div>
            </aside>
        </section>

        <section class="push-guide__instructions container" aria-labelledby="android-push-guide-title">
            <ol class="push-guide__steps push-guide__steps--single">
                <li class="push-guide__step bg-white">
                    <header class="push-guide__step-header d-flex align-items-start gap-3">
                        <span class="push-guide__step-number flex-shrink-0">1</span>
                        <div>
                            <p class="push-guide__step-label mb-1">{{$t('marketing.push_notifications_android.selection.label')}}</p>
                            <h2 class="h4 fw-bolder text-gray-900 mb-2">{{$t('marketing.push_notifications_android.selection.title')}}</h2>
                            <p class="text-gray-600 mb-0">{{$t('marketing.push_notifications_android.selection.description')}}</p>
                        </div>
                    </header>
                    <figure class="push-guide__figure mb-0">
                        <img
                        class="push-guide__image d-block"
                        src="/img/webpush/web-push-android-screen-1-activate-or-install-arrow-button.jpeg"
                        :alt="$t('marketing.push_notifications_android.selection.alt')"
                        width="623"
                        height="1280"
                        loading="eager"
                        decoding="async"
                        >
                    </figure>
                </li>
            </ol>

            <div class="push-guide__paths">
                <section v-for="path in paths" :key="path.key" class="push-guide__path">
                    <header class="push-guide__path-header text-center mx-auto">
                        <span class="push-guide__path-badge">{{path.badge}}</span>
                        <h2 class="h3 fw-bolder text-gray-900 mt-3 mb-2">{{path.title}}</h2>
                        <p class="text-gray-600 mb-0">{{path.description}}</p>
                    </header>
                    <ol class="push-guide__steps" :class="{'push-guide__steps--continuous': path.steps.length === 2}">
                        <li v-for="step in path.steps" :key="step.image" class="push-guide__step bg-white">
                            <header class="push-guide__step-header d-flex align-items-start gap-3">
                                <span class="push-guide__step-number push-guide__step-number--compact flex-shrink-0">{{step.badge}}</span>
                                <div>
                                    <p class="push-guide__step-label mb-1">{{step.label}}</p>
                                    <h3 class="h4 fw-bolder text-gray-900 mb-2">{{step.title}}</h3>
                                    <p class="text-gray-600 mb-0">{{step.description}}</p>
                                </div>
                            </header>
                            <figure class="push-guide__figure mb-0">
                                <img
                                class="push-guide__image d-block"
                                :src="step.image"
                                :alt="step.alt"
                                width="623"
                                height="1280"
                                loading="lazy"
                                decoding="async"
                                >
                            </figure>
                        </li>
                    </ol>
                </section>
            </div>

            <section class="push-guide__path push-guide__path--final">
                <header class="push-guide__path-header text-center mx-auto">
                    <span class="push-guide__path-badge">
                        <BootstrapIcon name="bell" :size="18" />
                    </span>
                    <h2 class="h3 fw-bolder text-gray-900 mt-3 mb-2">{{$t('marketing.push_notifications_android.activation.title')}}</h2>
                    <p class="text-gray-600 mb-0">{{$t('marketing.push_notifications_android.activation.description')}}</p>
                </header>
                <ol class="push-guide__steps push-guide__steps--continuous">
                    <li v-for="step in activationSteps" :key="step.image" class="push-guide__step bg-white">
                        <header class="push-guide__step-header d-flex align-items-start gap-3">
                            <span class="push-guide__step-number flex-shrink-0">{{step.badge}}</span>
                            <div>
                                <p class="push-guide__step-label mb-1">{{step.label}}</p>
                                <h3 class="h4 fw-bolder text-gray-900 mb-2">{{step.title}}</h3>
                                <p class="text-gray-600 mb-0">{{step.description}}</p>
                            </div>
                        </header>
                        <figure class="push-guide__figure mb-0">
                            <img
                            class="push-guide__image d-block"
                            :src="step.image"
                            :alt="step.alt"
                            width="623"
                            height="1280"
                            loading="lazy"
                            decoding="async"
                            >
                        </figure>
                    </li>
                </ol>
            </section>
        </section>

        <section class="push-guide__complete container text-center">
            <div class="push-guide__complete-card mx-auto">
                <span class="push-guide__complete-icon d-inline-flex align-items-center justify-content-center" aria-hidden="true">
                    <BootstrapIcon name="bell-fill" :size="24" />
                </span>
                <h2 class="h3 fw-bolder text-gray-900 mt-3 mb-2">{{$t('marketing.push_notifications_android.complete_title')}}</h2>
                <p class="text-gray-600 mx-auto mb-0">{{$t('marketing.push_notifications_android.complete_description')}}</p>
            </div>
        </section>
    </div>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import { TLD } from '@/common/constants.js'

export default {
    components: { BootstrapIcon },
    data: function () {
        return { TLD }
    },
    computed: {
        paths: function () {
            const definitions = [
                {
                    key: 'arrow',
                    badge: this.$t('marketing.push_notifications_android.paths.arrow.badge'),
                    images: [
                        ['2', 'web-push-android-screen-2-approve-install.jpeg'],
                        ['2.1', 'web-push-android-screen-2.1-confirm-install.jpeg']
                    ]
                },
                {
                    key: 'menu',
                    badge: this.$t('marketing.push_notifications_android.paths.menu.badge'),
                    images: [
                        ['3', 'web-push-android-screen-3-select-add-to.jpeg'],
                        ['3.1', 'web-push-android-screen-3-1-homescreen.jpeg'],
                        ['3.2', 'web-push-android-screen-3-2-confirm-homescreen.jpeg']
                    ]
                }
            ]

            return definitions.map(path => ({
                key: path.key,
                badge: path.badge,
                title: this.$t(`marketing.push_notifications_android.paths.${path.key}.title`),
                description: this.$t(`marketing.push_notifications_android.paths.${path.key}.description`),
                steps: path.images.map(([badge, image], index) => ({
                    badge,
                    image: `/img/webpush/${image}`,
                    label: this.$t(`marketing.push_notifications_android.paths.${path.key}.steps.${index + 1}.label`),
                    title: this.$t(`marketing.push_notifications_android.paths.${path.key}.steps.${index + 1}.title`),
                    description: this.$t(`marketing.push_notifications_android.paths.${path.key}.steps.${index + 1}.description`),
                    alt: this.$t(`marketing.push_notifications_android.paths.${path.key}.steps.${index + 1}.alt`)
                }))
            }))
        },
        activationSteps: function () {
            return [
                ['4', 'web-push-android-screen-4-activate-notifications.jpeg'],
                ['5', 'web-push-android-screen-5-activated.jpeg']
            ].map(([badge, image], index) => ({
                badge,
                image: `/img/webpush/${image}`,
                label: this.$t(`marketing.push_notifications_android.activation.steps.${index + 1}.label`),
                title: this.$t(`marketing.push_notifications_android.activation.steps.${index + 1}.title`),
                description: this.$t(`marketing.push_notifications_android.activation.steps.${index + 1}.description`),
                alt: this.$t(`marketing.push_notifications_android.activation.steps.${index + 1}.alt`)
            }))
        }
    }
}
</script>
